import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, ArrowRight, CheckCircle2, ClipboardList, Package,
         CalendarDays, Smartphone, Hammer, ShieldCheck, CreditCard, Wrench,
         MapPin, Lock } from "lucide-react";
import Screenshot, { BrowserFrame, PhoneFrame } from "../components/Screenshot";
import { SHOTS } from "../content/screenshots";
import { INDUSTRY_LIST } from "../content/industries";
import { TESTIMONIALS } from "../content/testimonials";
import { PROBLEMS } from "../content/sitePages";
import Seo from "../components/Seo";

export function StickyCta() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show || dismissed) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-slate-900/95 backdrop-blur border-t border-slate-700 px-4 py-3"
      data-testid="sticky-cta">
      <div className="max-w-6xl mx-auto flex items-center gap-3">
        <span className="text-sm text-slate-300 hidden sm:block flex-1">
          <b className="text-white">See Fieldistry on your own workflow.</b> Free trial, no card.</span>
        <Link to="/onboarding" data-testid="sticky-cta-trial"
          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold transition-colors">
          Start free trial</Link>
        <Link to="/assessment" data-testid="sticky-cta-assessment"
          className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:text-white text-sm font-semibold transition-colors">
          2-min assessment</Link>
        <button data-testid="sticky-cta-dismiss" onClick={() => setDismissed(true)}
          className="text-slate-500 hover:text-slate-300 text-lg leading-none px-1">×</button>
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

export function useUtm() {
  const [params] = useSearchParams();
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k.replace("utm_", "")] = v;
  });
  return utm;
}

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const links = [["Home", "/"], ["Features", "/features"], ["Industries", "/industries"],
    ["How It Works", "/how-it-works"], ["Pricing", "/pricing"], ["Setup Services", "/setup-services"],
    ["Resources", "/resources"]];
  return (
    <nav className="max-w-6xl mx-auto px-6 py-5" data-testid="public-nav">
      <div className="flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/icons/icon-192.png" alt="Fieldistry" className="h-9 w-9 rounded-lg" />
          <span className="font-heading font-bold text-white text-lg">Fieldistry</span>
        </Link>
        <div className="hidden lg:flex items-center gap-5 ml-4 text-sm text-slate-400">
          {links.map(([label, to]) => (
            <Link key={to} to={to} data-testid={`nav-${label.replace(/\s/g, "-").toLowerCase()}`}
              className="hover:text-white transition-colors">{label}</Link>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="hidden sm:block text-sm text-slate-300 hover:text-white transition-colors">Sign in</Link>
          <Link to="/book-demo" data-testid="nav-book-demo"
            className="hidden sm:block px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-500 text-sm font-semibold transition-colors">Book a Demo</Link>
          <Link to="/onboarding" data-testid="nav-start-trial"
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-semibold transition-colors">
            Start Free Trial</Link>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
            className="lg:hidden text-slate-300 p-2" aria-label="Menu">
            {open ? "✕" : "☰"}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden mt-4 rounded-xl bg-slate-900 border border-slate-800 p-4 space-y-1" data-testid="nav-mobile-menu">
          {links.map(([label, to]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)}
              className="block py-2.5 px-3 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white">{label}</Link>
          ))}
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <Link to="/login" onClick={() => setOpen(false)}
              className="flex-1 text-center py-2.5 rounded-lg border border-slate-700 text-sm text-slate-300">Sign in</Link>
            <Link to="/book-demo" onClick={() => setOpen(false)}
              className="flex-1 text-center py-2.5 rounded-lg border border-amber-500/50 text-amber-400 text-sm font-semibold">Book a Demo</Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-slate-800 mt-20">
      <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-lg bg-amber-500 flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-slate-900" />
            </div>
            <span className="font-heading font-bold text-white">Fieldistry</span>
          </div>
          <p className="text-sm text-slate-500">Business operations software for trade, service, construction and field-work companies.</p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Industries</div>
          <div className="grid grid-cols-2 gap-1.5">
            {INDUSTRY_LIST.map((i) => (
              <Link key={i.slug} to={`/${i.slug}`} data-testid={`footer-industry-${i.slug}`}
                className="text-sm text-slate-400 hover:text-amber-500 transition-colors">{i.name}</Link>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Get started</div>
          <div className="space-y-1.5">
            <Link to="/onboarding" className="block text-sm text-slate-400 hover:text-amber-500">Start a free trial</Link>
            <Link to="/book-demo" className="block text-sm text-slate-400 hover:text-amber-500">Book a demo</Link>
            <Link to="/setup-services" className="block text-sm text-slate-400 hover:text-amber-500">Setup services</Link>
            <Link to="/assessment" className="block text-sm text-slate-400 hover:text-amber-500">Free operations assessment</Link>
            <Link to="/faq" className="block text-sm text-slate-400 hover:text-amber-500">FAQ</Link>
            <Link to="/trust" className="block text-sm text-slate-400 hover:text-amber-500">Trust & security</Link>
            <Link to="/login" className="block text-sm text-slate-400 hover:text-amber-500">Sign in</Link>
          </div>
        </div>
      </div>
      <div className="text-center text-xs text-slate-600 pb-8">© 2026 Fieldistry. Enter information once. Update everything.</div>
    </footer>
  );
}

export function LeadForm({ interest = "demo", title = "See it in action", subtitle,
                     dark = true, testid = "lead-form" }) {
  const utm = useUtm();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "",
    industry: "", employees: "", problem: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/public/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, interest, utm, page: window.location.pathname }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      setDone(true);
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-8 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
        <div className="font-heading font-bold text-white text-xl">We'll be in touch within one business day.</div>
        <p className="text-slate-400 text-sm mt-2">Want to skip the wait? <Link to="/onboarding" className="text-amber-500 hover:underline">Start a trial now</Link>.</p>
      </div>
    );
  }

  const inputCls = `w-full rounded-lg border px-3.5 py-2.5 text-sm ${
    dark ? "bg-slate-900 border-slate-700 text-white placeholder:text-slate-500" : ""}`;
  return (
    <form onSubmit={submit} data-testid={testid}
      className={`rounded-2xl p-6 sm:p-8 ${dark ? "bg-slate-900 border border-slate-800" : "bg-white border border-slate-200"}`}>
      <h3 className="font-heading font-bold text-xl text-white">{title}</h3>
      {subtitle && <p className="text-slate-400 text-sm mt-1 mb-5">{subtitle}</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <input required data-testid={`${testid}-name`} placeholder="Your name*" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <input required data-testid={`${testid}-company`} placeholder="Company*" value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
        <input required type="email" data-testid={`${testid}-email`} placeholder="Email*" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <input data-testid={`${testid}-phone`} placeholder="Phone" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
        <select data-testid={`${testid}-industry`} value={form.industry}
          onChange={(e) => setForm({ ...form, industry: e.target.value })} className={inputCls}>
          <option value="">Industry…</option>
          {INDUSTRY_LIST.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
          <option value="other">Other field service</option>
        </select>
        <select data-testid={`${testid}-employees`} value={form.employees}
          onChange={(e) => setForm({ ...form, employees: e.target.value })} className={inputCls}>
          <option value="">Team size…</option>
          {["1-5", "6-15", "16-40", "41-100", "100+"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <textarea data-testid={`${testid}-problem`} rows={2} value={form.problem}
        onChange={(e) => setForm({ ...form, problem: e.target.value })}
        placeholder="What's your biggest operational headache right now?"
        className={`${inputCls} mt-3`} />
      <button type="submit" data-testid={`${testid}-submit`} disabled={busy}
        className="mt-4 w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold transition-colors">
        {busy ? "Sending…" : "Request demo"}
      </button>
      <p className="text-[11px] text-slate-500 mt-3 text-center">We'll only use your details to contact you about Fieldistry. No spam.</p>
    </form>
  );
}

export default function Marketing() {
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/billing/plans`).then((r) => r.json()).then((d) => setPlans(d.plans || [])).catch(() => {});
    fetch(`${API}/api/billing/setup-packages`).then((r) => r.json()).then(setPackages).catch(() => {});
  }, []);

  const HOW = [
    ["Capture", "Leads and customer requests enter one pipeline, nothing lives in an inbox.", ClipboardList],
    ["Prepare", "Readiness engine: people, materials, equipment and customer confirmation must line up before work is READY.", Package],
    ["Schedule", "Crews, techs and equipment on one calendar with conflict warnings.", CalendarDays],
    ["Execute", "Field teams run My Day on their phones: checklists, photos, time, issues.", Smartphone],
    ["Complete", "Completion requirements enforced → ready to bill → invoice without chasing paperwork.", CheckCircle2],
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="marketing-home">
      <Seo path="/" />
      <PublicNav />

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-widest mb-6">
            <Hammer className="h-3.5 w-3.5" /> For trade & field-service companies
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="marketing-headline">
            Run your entire trades business from <span className="text-amber-500">one system.</span>
          </h1>
          <p className="text-slate-400 text-lg mt-6 max-w-lg">
            Fieldistry connects your leads, estimates, customers, jobs, crews, documents, invoices and follow-ups, 
            <b className="text-slate-200"> so nothing gets lost between the office and the field.</b>
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/onboarding" data-testid="cta-start-trial"
              className="px-6 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold transition-colors">
              Start Free Trial</Link>
            <Link to="/book-demo" data-testid="cta-book-demo"
              className="px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 font-semibold transition-colors">
              Book a Demo</Link>
            <Link to="/setup-services" data-testid="cta-dfy"
              className="text-amber-400 hover:text-amber-300 font-semibold text-sm transition-colors">
              Have Fieldistry set it up for me →</Link>
          </div>
          <p className="text-slate-500 text-sm mt-6 italic">"You don't have to change your business to fit our software. We configure the software around how your business operates."</p>
        </div>
        <div className="hidden lg:block relative" data-testid="hero-product-mockup">
          <BrowserFrame url="app.fieldistry.com/command">
            <Screenshot src={SHOTS.command.src} alt={SHOTS.command.alt} testid="hero-shot-command"
              className="w-full aspect-[16/10] object-cover object-top" />
          </BrowserFrame>
          <div className="absolute -bottom-8 -right-3 w-36 xl:w-44 drop-shadow-2xl">
            <PhoneFrame>
              <Screenshot src={SHOTS.field.src} alt={SHOTS.field.alt} testid="hero-shot-field"
                className="w-full aspect-[9/19] object-cover object-top" />
            </PhoneFrame>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-800/60 bg-slate-900/30" data-testid="trust-bar">
        <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            [ShieldCheck, "Free trial", "No card required to start"],
            [Wrench, "Done-for-you setup", "We configure it with you"],
            [CreditCard, "Secure billing", "Stripe-processed, in CAD"],
            [Lock, "Your data, private", "Never sold or shared"],
            [MapPin, "Built for Canadian trades", "Field-service first"],
          ].map(([Icon, title, sub]) => (
            <div key={title} className="flex items-start gap-2.5" data-testid={`trust-item-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-100 leading-tight">{title}</div>
                <div className="text-xs text-slate-500 leading-tight mt-0.5">{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="how" className="border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="marketing-how">How it works</h2>
          <p className="text-slate-400 mt-3 max-w-2xl">Enter information once. The platform updates everything and tells the right person what's next.</p>
          <div className="grid md:grid-cols-5 gap-4 mt-10">
            {HOW.map(([title, text, Icon], i) => (
              <div key={title} className="relative p-5 rounded-2xl bg-slate-900 border border-slate-800">
                <div className="text-amber-500 font-mono text-xs font-bold mb-2">{i + 1}</div>
                <Icon className="h-5 w-5 text-slate-400 mb-2" />
                <div className="font-semibold">{title}</div>
                <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="product-gallery-title">
            Don't take our word for it, <span className="text-amber-500">look at it.</span></h2>
          <p className="text-slate-400 mt-3 max-w-2xl">Real screens from a live Fieldistry workspace. Click any image to view it full-size.</p>
          <div className="grid sm:grid-cols-2 gap-5 mt-10">
            {[SHOTS.schedule, SHOTS.jobs, SHOTS.customers, SHOTS.myWork].map((s) => (
              <figure key={s.key} className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
                <Screenshot src={s.src} alt={s.alt} testid={`gallery-shot-${s.key}`}
                  className="w-full aspect-[16/10] object-cover object-top" />
                <figcaption className="p-4">
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.caption}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl" data-testid="problems-title">
            Your business shouldn't need five systems to complete one job.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
            {PROBLEMS.map((p) => (
              <div key={p} className="flex items-start gap-3 p-4 rounded-xl bg-slate-900 border border-slate-800">
                <div className="h-2 w-2 rounded-full bg-rose-500 mt-2 shrink-0" />
                <p className="text-sm text-slate-300">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="industries" className="max-w-6xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Built around your industry</h2>
        <p className="text-slate-400 mt-3 max-w-2xl">Same platform, configured to how your trade actually runs, workflows, job types, checklists and terminology.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-10">
          {INDUSTRY_LIST.map((i) => (
            <Link key={i.slug} to={`/${i.slug}`} data-testid={`industry-card-${i.slug}`}
              className="group p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-amber-500/50 transition-colors">
              <div className="font-semibold group-hover:text-amber-400 transition-colors">{i.name}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                See the workflow <ArrowRight className="h-3 w-3" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="testimonials-title">
            Operators who run on Fieldistry</h2>
          <p className="text-slate-400 mt-3">Real workflows, real crews, configured around how each company already worked.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <figure key={t.name} data-testid={`testimonial-${i}`}
                className="p-6 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col">
                <blockquote className="text-sm text-slate-300 leading-relaxed flex-1">“{t.quote}”</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-slate-800">
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}, {t.company}</div>
                  <div className="text-xs font-bold text-amber-500 mt-1.5">{t.metric}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Simple plans, configured to you</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {plans.map((p) => (
              <div key={p.key} data-testid={`marketing-plan-${p.key}`}
                className="p-6 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col">
                <div className="font-heading font-bold text-lg">{p.name}</div>
                <div className="mt-2"><span className="font-heading text-3xl font-bold">${Number(p.monthly_price).toLocaleString()}</span>
                  <span className="text-slate-500 text-sm">/mo</span></div>
                <div className="text-xs text-slate-500 mt-1">or ${Number(p.annual_price).toLocaleString()}/yr</div>
                <p className="text-sm text-slate-400 mt-3 flex-1">{p.description}</p>
                <Link to={`/onboarding?plan=${p.key}`} data-testid={`marketing-plan-cta-${p.key}`}
                  className="mt-4 text-center py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold transition-colors">
                  Subscribe, {p.name}</Link>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-4">Custom / Enterprise arrangements available. Every plan starts with a free trial.</p>
        </div>
      </section>

      <section id="setup" className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="marketing-dfy">
              WE'LL BUILD YOUR WORKSPACE <span className="text-amber-500">FOR YOU.</span>
            </h2>
            <p className="text-slate-400 mt-4">Done-for-You setup: send us your employee list, customers, suppliers and how your business works. Our implementation team configures your workflow, roles, forms and automations, you review, we train your team, you go live.</p>
            <div className="space-y-3 mt-6">
              {packages.map((p) => (
                <div key={p.key} data-testid={`marketing-package-${p.key}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-900 border border-slate-800">
                  <div className="flex-1">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500">{(p.includes || []).slice(0, 3).join(" · ")}</div>
                  </div>
                  <div className="font-heading font-bold text-lg">${Number(p.price).toLocaleString()}{p.key === "branded_setup" ? "+" : ""}</div>
                </div>
              ))}
            </div>
          </div>
          <LeadForm interest="setup" title="Get it set up for me"
            subtitle="Tell us about your company, we'll propose a setup package."
            testid="dfy-lead-form" />
        </div>
      </section>

      <section className="border-t border-slate-800/60 bg-slate-900/40">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="final-cta">
            Pick how you want to start.</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-10 text-left">
            <Link to="/onboarding" data-testid="funnel-trial"
              className="p-6 rounded-2xl bg-slate-900 border border-amber-500/50 hover:border-amber-500 transition-colors group">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-500">Fastest</div>
              <div className="font-heading font-bold text-lg mt-1 group-hover:text-amber-400 transition-colors">Start a free trial</div>
              <p className="text-xs text-slate-400 mt-2">Explore with a sample business or set up your own. No card required.</p>
            </Link>
            <Link to="/assessment" data-testid="funnel-assessment"
              className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors group">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">2 minutes</div>
              <div className="font-heading font-bold text-lg mt-1 group-hover:text-amber-400 transition-colors">Take the assessment</div>
              <p className="text-xs text-slate-400 mt-2">See where operational chaos is costing you hours, honest snapshot, no fluff.</p>
            </Link>
            <a href="#setup" data-testid="funnel-dfy"
              className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors group">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Hands-off</div>
              <div className="font-heading font-bold text-lg mt-1 group-hover:text-amber-400 transition-colors">Get it set up for me</div>
              <p className="text-xs text-slate-400 mt-2">Send us your info, we configure, you review, your team gets trained, you go live.</p>
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
      <StickyCta />
    </div>
  );
}
